import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACTIVE_ATHLETE_KEY = 'cricket_active_athlete_id';

const HouseholdContext = createContext();

export const useHousehold = () => {
  const ctx = useContext(HouseholdContext);
  if (!ctx) throw new Error('useHousehold must be used within HouseholdProvider');
  return ctx;
};

/**
 * Loads the household + athletes for the current account and exposes
 * an "active athlete" selector (so the dashboard, video upload, and
 * provider widgets all read from the same source).
 */
export const HouseholdProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [household, setHousehold] = useState(null);
  const [members, setMembers] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [activeAthleteId, setActiveAthleteIdState] = useState(
    () => localStorage.getItem(ACTIVE_ATHLETE_KEY),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setActiveAthleteId = useCallback((id) => {
    if (id) localStorage.setItem(ACTIVE_ATHLETE_KEY, id);
    else localStorage.removeItem(ACTIVE_ATHLETE_KEY);
    setActiveAthleteIdState(id);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/v2/household`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHousehold(res.data.household);
      setMembers(res.data.members || []);
      setAthletes(res.data.athletes || []);
      // Pick a default active athlete if none stored.
      const stored = localStorage.getItem(ACTIVE_ATHLETE_KEY);
      const stillValid = stored && (res.data.athletes || []).some((a) => a.id === stored);
      if (!stillValid) {
        const first = (res.data.athletes || [])[0];
        if (first) setActiveAthleteId(first.id);
        else setActiveAthleteId(null);
      }
      setError(null);
    } catch (err) {
      // 404 just means the household isn't set up yet; not an error to bubble.
      if (err.response?.status !== 404) {
        setError(err.response?.data?.detail || 'household_fetch_failed');
      }
      setHousehold(null);
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, setActiveAthleteId]);

  useEffect(() => { refresh(); }, [refresh]);

  const addAthlete = async (payload) => {
    const res = await axios.post(`${API}/auth/v2/household/athletes`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await refresh();
    return res.data;
  };

  const linkProvider = async (athleteId, provider, externalId, handle) => {
    const res = await axios.post(
      `${API}/auth/v2/household/athletes/${athleteId}/link-provider`,
      { provider, external_id: externalId, handle },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    await refresh();
    return res.data;
  };

  const importProviderCsv = async (athleteId, provider, csvText) => {
    const res = await axios.post(
      `${API}/providers/${provider}/import`,
      csvText,
      {
        params: { athlete_id: athleteId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'text/csv',
        },
      },
    );
    return res.data;
  };

  const inviteMember = async (phone, role = 'guardian') => {
    const res = await axios.post(`${API}/auth/v2/household/invite`, { phone, role }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await refresh();
    return res.data;
  };

  const activeAthlete = athletes.find((a) => a.id === activeAthleteId) || athletes[0] || null;

  return (
    <HouseholdContext.Provider value={{
      household,
      members,
      athletes,
      activeAthlete,
      activeAthleteId: activeAthlete?.id || null,
      setActiveAthleteId,
      loading,
      error,
      refresh,
      addAthlete,
      linkProvider,
      importProviderCsv,
      inviteMember,
    }}>
      {children}
    </HouseholdContext.Provider>
  );
};
